import { experiences, experienceBySlug } from '../data/experiences';
import { whatsappLink, contact } from '../data/site';

/**
 * WebMCP — exposes a handful of read-only "tools" to AI agents that visit the
 * page, via the experimental `navigator.modelContext` API.
 *
 * The spec is still emerging and ships behind different shapes in different
 * agents, so we feature-detect and support both the `registerTool` (one call
 * per tool) and `provideContext` (batch) variants, and silently no-op when the
 * API is absent. Everything here is derived from the same data the UI renders,
 * so an agent sees exactly what a human does.
 */

const SITE_ORIGIN = 'https://flyingmuse.gr';

interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<unknown> | unknown;
}

interface ModelContext {
  registerTool?: (tool: WebMcpTool) => void;
  provideContext?: (ctx: { tools: WebMcpTool[] }) => void;
}

/** Wraps a value as the text-content result shape agents expect. */
function asResult(data: unknown) {
  return { content: [{ type: 'text', text: JSON.stringify(data, null, 2) }] };
}

function experienceSummary(slug: string) {
  const exp = experienceBySlug(slug);
  if (!exp) return null;
  return {
    title: exp.pageTitle,
    slug: exp.slug,
    url: `${SITE_ORIGIN}/${exp.slug}`,
    kicker: exp.kicker,
    description: exp.cardDescription,
    intro: exp.intro,
    fromPrice: `€${exp.fromPrice}`,
    audience: exp.audience.map((a) => a.label),
    included: exp.included,
    idealFor: exp.idealFor ?? [],
    addOns: exp.addOns ?? [],
    packages: exp.packages.map((g) => ({
      title: g.title,
      description: g.description,
      note: g.note,
      rows: g.rows.map((r) => ({
        guests: r.guests,
        duration: r.duration,
        price: r.price,
        includes: r.includes,
      })),
    })),
    delivery: exp.deliveryNote,
    bookingLink: whatsappLink(
      `Hi Flying Muse! I'm interested in the ${exp.pageTitle}. Could you share availability and the next steps?`,
    ),
  };
}

const tools: WebMcpTool[] = [
  {
    name: 'list_experiences',
    description:
      'List all Flying Muse Santorini photoshoot experiences with their title, page URL and starting price.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () =>
      asResult(
        experiences.map((exp) => ({
          title: exp.pageTitle,
          slug: exp.slug,
          url: `${SITE_ORIGIN}/${exp.slug}`,
          description: exp.cardDescription,
          fromPrice: `€${exp.fromPrice}`,
        })),
      ),
  },
  {
    name: 'get_experience_details',
    description:
      'Get full details for one experience — intro, what is included, packages, pricing and the booking link. Pass the slug from list_experiences.',
    inputSchema: {
      type: 'object',
      properties: {
        slug: {
          type: 'string',
          description: 'The experience slug, e.g. "flying-dress-photoshoot-santorini".',
          enum: experiences.map((e) => e.slug),
        },
      },
      required: ['slug'],
      additionalProperties: false,
    },
    execute: (args) => {
      const slug = typeof args?.slug === 'string' ? args.slug : '';
      const summary = experienceSummary(slug);
      return asResult(
        summary ?? { error: `Unknown experience slug: "${slug}". Use list_experiences first.` },
      );
    },
  },
  {
    name: 'get_booking_link',
    description:
      'Get the WhatsApp booking link for Flying Muse, optionally pre-filled with a message about a specific experience.',
    inputSchema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description: 'Optional message to pre-fill in WhatsApp.',
        },
      },
      additionalProperties: false,
    },
    execute: (args) => {
      const message = typeof args?.message === 'string' ? args.message : undefined;
      return asResult({
        whatsapp: whatsappLink(message),
        phone: contact.phone,
        email: contact.email,
        location: contact.location,
      });
    },
  },
];

/** Registers the Flying Muse tools with the agent runtime, if one is present. */
export function registerWebMcpTools(): void {
  if (typeof navigator === 'undefined') return;
  const ctx = (navigator as Navigator & { modelContext?: ModelContext }).modelContext;
  if (!ctx) return;

  try {
    if (typeof ctx.provideContext === 'function') {
      ctx.provideContext({ tools });
    } else if (typeof ctx.registerTool === 'function') {
      tools.forEach((tool) => ctx.registerTool!(tool));
    }
  } catch (e) {
    console.warn('WebMCP tool registration failed:', e);
  }
}
